import styled from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useListEconRunsQuery } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import {
  ECON_MODULE_IDS,
  ECON_MODULES,
  fmtPeriod,
  isEconModuleId,
  isRunActive,
  statusLabel,
  statusTone,
} from '@entities/economic/types';
import { BdrReport } from './BdrReport';
import { MiningReport } from './MiningReport';
import { CanteenReport } from './CanteenReport';
import { CashflowReport } from './CashflowReport';
import { ExecPackReport } from './ExecPackReport';

const ModuleTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const Tab = styled(Link)`
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const RunRow = styled(Card)`
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
`;

/** Автор обработки: список общий для отдела (Д12). */
const RunAuthor = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RunModule = styled(Badge)`
  flex-shrink: 0;
`;

const RunPeriod = styled.div`
  font-weight: 600;
  font-size: 14px;
  min-width: 120px;
`;

const RunScope = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RunDate = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  flex-shrink: 0;
`;

const Empty = styled(Card)`
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
  padding: ${({ theme }) => theme.spacing(8)} ${({ theme }) => theme.spacing(4)};
`;

/** Сводный список всех обработок (когда модуль не выбран) — реестр поверх всех
 *  направлений, общий для отдела (у каждой — автор). Клик ведёт в карточку. */
function AllRunsRegistry() {
  const navigate = useNavigate();
  // Статус/прогресс обработок прилетает по WS-событию `econ_run_updated`
  // (rtkBridge инвалидирует generic-тег 'EconRun' → RTK перетягивает реестр).
  // Частый poll не нужен — оставляем редкий страховочный рефетч (60с) на
  // случай потери WS-соединения.
  const { data, isLoading } = useListEconRunsQuery({}, { pollingInterval: 60000 });
  const runs = data?.items ?? [];

  if (isLoading) {
    return <Empty>Загрузка обработок…</Empty>;
  }
  if (runs.length === 0) {
    return <Empty>Обработок пока нет. Выберите модуль выше и запустите первый расчёт.</Empty>;
  }
  return (
    <Stack>
      {runs.map((r) => {
        const short = isEconModuleId(r.module) ? ECON_MODULES[r.module].short : r.module;
        return (
          <RunRow
            key={r.id}
            $accent
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/agents/econ/runs/${r.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/agents/econ/runs/${r.id}`);
              }
            }}
          >
            <RunModule $tone="neutral">{short}</RunModule>
            <RunPeriod>{fmtPeriod(r.period)}</RunPeriod>
            <RunScope>{r.object_scope ?? '—'}</RunScope>
            <RunAuthor title="Автор обработки">{r.author_name ?? 'система'}</RunAuthor>
            {isRunActive(r.status) && <RunDate>{r.progress_percent}%</RunDate>}
            <Badge $tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
            <RunDate>{new Date(r.created_at).toLocaleDateString('ru-RU')}</RunDate>
          </RunRow>
        );
      })}
    </Stack>
  );
}

const MODULE_VIEW = {
  bdr: BdrReport,
  mining: MiningReport,
  canteen: CanteenReport,
  cashflow: CashflowReport,
  exec_pack: ExecPackReport,
} as const;

/**
 * Роут `/agents/econ/runs`. Модуль приходит в `?module=`; если он валиден —
 * рендерим соответствующую модульную страницу (форма загрузки + список её
 * обработок). Без модуля — сводный реестр всех обработок + переключатель.
 */
export function RunsList() {
  const [params] = useSearchParams();
  const moduleId = params.get('module');
  const activeModule = moduleId && isEconModuleId(moduleId) ? moduleId : null;

  return (
    <div>
      <ModuleTabs>
        <Tab
          to="/agents/econ/runs"
          aria-current={activeModule === null ? 'page' : undefined}
          style={activeModule === null ? { borderColor: 'currentColor' } : undefined}
        >
          Все обработки
        </Tab>
        {ECON_MODULE_IDS.map((id) => (
          <Tab
            key={id}
            to={`/agents/econ/runs?module=${id}`}
            aria-current={activeModule === id ? 'page' : undefined}
            style={activeModule === id ? { borderColor: 'currentColor' } : undefined}
          >
            {ECON_MODULES[id].short}
          </Tab>
        ))}
      </ModuleTabs>

      {activeModule ? (
        (() => {
          const View = MODULE_VIEW[activeModule];
          return <View />;
        })()
      ) : (
        <AllRunsRegistry />
      )}
    </div>
  );
}
